import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, doc, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useOutlet } from '../../context/OutletContext';

const TableContext = createContext();

export const useTable = () => {
  const context = useContext(TableContext);
  if (!context) throw new Error('useTable must be used within TableProvider');
  return context;
};

export const TableProvider = ({ children }) => {
  const { selectedOutlet } = useOutlet(); // Get selected outlet
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load tables for selected outlet
  useEffect(() => {
    // Wait for outlet to be selected
    if (!selectedOutlet) {
      setLoading(false);
      return;
    }

    console.log(`🍽️ Loading tables for outlet: ${selectedOutlet.name}`);

    const q = query(
      collection(db, 'tables'),
      where('outletId', '==', selectedOutlet.id),
      orderBy('tableNumber')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tablesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log(`📊 Loaded ${tablesData.length} tables for ${selectedOutlet.name}`);
      setTables(tablesData);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [selectedOutlet]);

  // Load orders for selected outlet
  useEffect(() => {
    // Wait for outlet to be selected
    if (!selectedOutlet) {
      return;
    }

    console.log(`📋 Loading dine-in orders for outlet: ${selectedOutlet.name}`);

    const q = query(
      collection(db, 'orders'),
      where('outletId', '==', selectedOutlet.id),
      where('orderType', '==', 'dine-in'),
      where('status', 'in', ['pending', 'in_progress', 'prepared', 'served']),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log(`📊 Loaded ${ordersData.length} active dine-in orders for ${selectedOutlet.name}`);
      setOrders(ordersData);
    });
    return () => unsubscribe();
  }, [selectedOutlet]);

  const getTable = (tableId) => tables.find(t => t.id === tableId);
  const getTableOrders = (tableId) => orders.filter(o => o.tableId === tableId);

  const createTableOrder = async (tableId, orderData) => {
    try {
      const table = getTable(tableId);

      if (!table) {
        return { success: false, error: 'Table not found' };
      }

      // Ensure outlet information is included
      if (!selectedOutlet) {
        return { success: false, error: 'No outlet selected' };
      }

      const orderRef = await addDoc(collection(db, 'orders'), {
        ...orderData,
        tableId,
        tableNumber: table.tableNumber,
        outletId: selectedOutlet.id,
        outletName: selectedOutlet.name,
        orderType: 'dine-in',
        status: 'pending',
        timestamp: new Date()
      });

      await updateDoc(doc(db, 'tables', tableId), {
        status: 'occupied',
        currentOrder: orderRef.id
      });

      console.log(`✅ Created dine-in order for table ${table.tableNumber} at ${selectedOutlet.name}`);
      return { success: true, orderId: orderRef.id };
    } catch (error) {
      console.error('Error creating table order:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    tables,
    orders,
    loading,
    getTable,
    getTableOrders,
    createTableOrder
  };

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
};
